import React from 'react';
import styles from './HardwareSupport.module.css';

const HardwareSupport = () => {
  const handleCTAClick = (action) => {
    console.log(`${action} clicked`);
    // Add your navigation/modal logic here
  };

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroHeadline}>
            AI-Powered Hardware Assistance, Anytime, Anywhere
          </h1>
          <p className={styles.heroSubtext}>
            Toxbox AI monitors, diagnoses, and resolves hardware issues instantly—keeping 
            your devices and servers running flawlessly.
          </p>
          <div className={styles.ctaButtons}>
            <button 
              className={`${styles.ctaButton} ${styles.ctaPrimary}`}
              onClick={() => handleCTAClick('Get Started')}
            >
              Get Started
            </button>
            <button 
              className={`${styles.ctaButton} ${styles.ctaSecondary}`}
              onClick={() => handleCTAClick('Request a Demo')}
            >
              Request a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose AI Hardware Support */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Why Choose AI Hardware Support?</h2>
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🔍</div>
              <h3>24/7 Device Monitoring</h3>
              <p>Detect issues before they affect performance.</p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🤖</div>
              <h3>Automated Diagnostics</h3>
              <p>AI identifies faults in PCs, servers, and peripherals.</p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>⚡</div>
              <h3>Reduced Downtime</h3>
              <p>Minimize hardware failures and interruptions.</p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>💰</div>
              <h3>Cost-Efficient Maintenance</h3>
              <p>Prevent expensive manual troubleshooting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className={`${styles.section} ${styles.keyFeaturesSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Key Features</h2>
          <div className={styles.featuresList}>
            <div className={styles.featureItem}>
              <h4>📊 Real-Time Hardware Monitoring</h4>
              <p>CPU, GPU, RAM, storage, network devices.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>🔮 Predictive Maintenance</h4>
              <p>AI predicts potential failures before they happen.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>💻 Multi-Device Support</h4>
              <p>PCs, laptops, servers, IoT devices.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>🚨 Automatic Alerts</h4>
              <p>Notifications for hardware anomalies.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>📈 Detailed Analytics</h4>
              <p>Track performance, health, and uptime trends.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={`${styles.section} ${styles.howItWorksSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
          <div className={styles.stepsContainer}>
            {[
              "AI continuously monitors your hardware.",
              "Detects issues like overheating, low memory, disk errors, or failures.",
              "Automatically resolves minor issues or provides actionable steps.",
              "Escalates critical problems to human technicians.",
              "Provides real-time updates and detailed reports."
            ].map((step, index) => (
              <div key={index} className={styles.stepItem}>
                <div className={styles.stepNumber}>{index + 1}</div>
                <div className={styles.stepContent}>
                  <p className={styles.stepText}>{step}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Benefits</h2>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>⚡</div>
              <h4>Faster Hardware Resolution</h4>
              <p>Instant detection and automated fixes.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>🛡️</div>
              <h4>Proactive Maintenance</h4>
              <p>Avoid major failures before they occur.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>💲</div>
              <h4>Lower IT Costs</h4>
              <p>Reduce manual intervention and service calls.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>📊</div>
              <h4>Actionable Insights</h4>
              <p>Improve hardware lifecycle management.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Hardware */}
      <section className={`${styles.section} ${styles.supportedHardwareSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Supported Hardware</h2>
          <div className={styles.hardwareGrid}>
            <div className={styles.hardwareCategory}>
              <div className={styles.categoryIcon}>💻</div>
              <h4>PCs & Laptops</h4>
              <p>Windows, macOS, Linux machines.</p>
            </div>
            <div className={styles.hardwareCategory}>
              <div className={styles.categoryIcon}>🖥️</div>
              <h4>Servers & Data Centers</h4>
              <p>Monitoring and maintenance of enterprise-grade servers.</p>
            </div>
            <div className={styles.hardwareCategory}>
              <div className={styles.categoryIcon}>🌐</div>
              <h4>Networking Devices</h4>
              <p>Routers, switches, firewalls, IoT devices.</p>
            </div>
            <div className={styles.hardwareCategory}>
              <div className={styles.categoryIcon}>🖨️</div>
              <h4>Peripheral Devices</h4>
              <p>Printers, scanners, storage devices.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Insights & Recommendations */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>AI Insights & Recommendations</h2>
          <div className={styles.insightsList}>
            <div className={styles.insightItem}>
              <div className={styles.insightIcon}>📊</div>
              <p>Analyze hardware performance trends.</p>
            </div>
            <div className={styles.insightItem}>
              <div className={styles.insightIcon}>🔧</div>
              <p>Suggest upgrades and preventive maintenance.</p>
            </div>
            <div className={styles.insightItem}>
              <div className={styles.insightIcon}>⚠️</div>
              <p>Highlight potential risks before they become critical.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Human Technician Collaboration */}
      <section className={`${styles.section} ${styles.collaborationSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Human Technician Collaboration</h2>
          <div className={styles.collaborationList}>
            <div className={styles.collaborationItem}>
              <div className={styles.collaborationIcon}>🔄</div>
              <p>Escalate complex hardware failures to certified IT staff.</p>
            </div>
            <div className={styles.collaborationItem}>
              <div className={styles.collaborationIcon}>📋</div>
              <p>Provide complete diagnostic history to accelerate repair.</p>
            </div>
            <div className={styles.collaborationItem}>
              <div className={styles.collaborationIcon}>🤝</div>
              <p>Ensure seamless collaboration between AI and human experts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Industry Applications</h2>
          <div className={styles.industryGrid}>
            <div className={styles.industryCard}>
              <div className={styles.industryIcon}>🏢</div>
              <h4>Enterprise IT</h4>
              <p>Employee workstation and server monitoring.</p>
            </div>
            <div className={styles.industryCard}>
              <div className={styles.industryIcon}>🏭</div>
              <h4>Data Centers</h4>
              <p>Prevent downtime with AI predictive analysis.</p>
            </div>
            <div className={styles.industryCard}>
              <div className={styles.industryIcon}>⚙️</div>
              <h4>Manufacturing</h4>
              <p>IoT devices and industrial hardware maintenance.</p>
            </div>
            <div className={styles.industryCard}>
              <div className={styles.industryIcon}>🏥</div>
              <h4>Education & Healthcare</h4>
              <p>Device support for labs and patient systems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className={`${styles.section} ${styles.testimonialsSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>What Our Customers Say</h2>
          <div className={styles.testimonialsGrid}>
            <div className={styles.testimonialCard}>
              <blockquote>
                "AI detected server issues before they caused downtime."
              </blockquote>
              <cite>– Data Center Admin</cite>
            </div>
            <div className={styles.testimonialCard}>
              <blockquote>
                "Hardware maintenance has never been easier."
              </blockquote>
              <cite>– IT Manager</cite>
            </div>
            <div className={styles.testimonialCard}>
              <blockquote>
                "Predictive alerts saved us thousands in replacement costs."
              </blockquote>
              <cite>– Enterprise Client</cite>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className={styles.ctaSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.ctaHeadline}>Take Control of Your Hardware with AI</h2>
          <p className={styles.ctaSubtext}>
            Toxbox AI ensures your devices, servers, and networks stay healthy and productive.
          </p>
          <div className={styles.ctaButtons}>
            <button 
              className={`${styles.ctaButton} ${styles.ctaPrimary}`}
              onClick={() => handleCTAClick('Request a Demo')}
            >
              Request a Demo
            </button>
            <button 
              className={`${styles.ctaButton} ${styles.ctaSecondary}`}
              onClick={() => handleCTAClick('Get Started Now')}
            >
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Security & Compliance</h2>
          <div className={styles.securityList}>
            <div className={styles.securityItem}>
              <div className={styles.securityIcon}>🔒</div>
              <p>Secure monitoring and diagnostics with end-to-end encryption.</p>
            </div>
            <div className={styles.securityItem}>
              <div className={styles.securityIcon}>✅</div>
              <p>GDPR, ISO, and IT compliance ready.</p>
            </div>
            <div className={styles.securityItem}>
              <div className={styles.securityIcon}>🌍</div>
              <p>Trusted by enterprises worldwide for hardware reliability.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HardwareSupport;
